// PricingCard — tarjeta de compra (adaptación del bloque shadcn que pasó
// dirección). Cambios respecto al original:
//   - Sin dependencias nuevas: framer-motion y lucide-react ya existen en el
//     proyecto; Card/Separator son divs propios (no radix) y no hay cva.
//   - Paleta shadcn (primary/muted/card...) traducida a los tokens ds-*.
//   - `price` acepta string formateado + moneda + sufijo (MXN/USD, "al año")
//     en vez del "$price one-time payment" fijo del original.
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useInView , type Variants} from "framer-motion";
import { Check, Loader2 } from "lucide-react";
import { cn } from "@/shared/lib/cn";

export interface PricingFeature {
  title: string;
  items: string[];
}

export interface PricingCardProps {
  title: string;
  description: string;
  /** Precio ya formateado (p.ej. "$6,000"). */
  price: string;
  currency: string;
  /** Sufijo bajo el precio (p.ej. "MXN al año · por usuario"). */
  priceSuffix: string;
  originalPrice?: string;
  features: PricingFeature[];
  buttonText?: string;
  busy?: boolean;
  onButtonClick?: () => void;
  /**
   * Destino, cuando la acción NAVEGA en vez de hacer algo.
   *
   * Con `buttonTo` la tarjeta pinta un `<Link>` de verdad, así que
   * funcionan el clic con rueda, el "abrir en pestaña nueva" y el menú
   * contextual, y un lector de pantalla lo anuncia como enlace. Sin él se
   * pinta el `<button>` de siempre, que es lo que le toca a algo que
   * dispara un proceso —generar un cobro— y no lleva a ninguna dirección.
   *
   * Existe porque la landing pública de franquicias todavía no tiene
   * checkout: su acción es ir a `/register`. Cuando lo tenga, esa página
   * pasa a `onButtonClick` y esta prop deja de usarse ahí.
   */
  buttonTo?: string;
  className?: string;
}

const containerVariants: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1, delayChildren: 0.3 },
  },
};

const itemVariants: Variants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: { type: "spring", stiffness: 260, damping: 20 },
  },
};

const listItemVariants: Variants = {
  hidden: { opacity: 0, x: -20 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { type: "spring", stiffness: 100, damping: 10 },
  },
};

/**
 * La píldora de acción. Una sola definición para las dos formas que puede
 * tomar —enlace o botón—, para que no diverjan al retocar el diseño.
 *
 * `active:scale` se apaga con `motion-reduce`, y el anillo de foco es
 * explícito: sin él, `outline-none` heredado del reset dejaba la acción
 * principal de la tarjeta sin ninguna señal al llegar por teclado.
 */
const ACTION_CLASSES =
  "inline-flex w-full items-center justify-center gap-2 rounded-full bg-ds-violet-pop px-6 py-3.5 text-base font-semibold text-white shadow-lg shadow-ds-violet-pop/25 transition hover:brightness-110 active:scale-[0.98] disabled:opacity-60 motion-reduce:transform-none motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ds-violet-pop/50 focus-visible:ring-offset-2";

export function PricingCard({
  title,
  description,
  price,
  currency,
  priceSuffix,
  originalPrice,
  features,
  buttonText = "Comenzar",
  busy = false,
  onButtonClick,
  buttonTo,
  className,
}: PricingCardProps) {
  const containerRef = useRef(null);
  const isInView = useInView(containerRef, { once: true, amount: 0.2 });
  const [hasAnimated, setHasAnimated] = useState(false);

  useEffect(() => {
    if (isInView && !hasAnimated) setHasAnimated(true);
  }, [isInView, hasAnimated]);

  return (
    <motion.section
      ref={containerRef}
      className={cn("w-full py-6", className)}
      initial="hidden"
      animate={hasAnimated ? "visible" : "hidden"}
      variants={containerVariants}
    >
      <div className="relative mx-auto w-full max-w-5xl overflow-hidden rounded-[28px] border border-ds-border bg-white shadow-ds-card">
        <div className="flex flex-col lg:flex-row">
          {/* Panel izquierdo: título, precio y botón de pago */}
          <motion.div
            className="flex flex-col justify-between p-6 text-left lg:w-2/5 lg:p-10"
            variants={itemVariants}
          >
            <div>
              <h3 className="text-3xl font-semibold tracking-tight text-gray-900">
                {title}
              </h3>
              <p className="mt-2 text-sm text-gray-500">{description}</p>

              <motion.div className="mt-6 space-y-2" variants={itemVariants}>
                <div className="flex items-baseline gap-2">
                  <span className="text-5xl font-extrabold tracking-tight text-gray-900">
                    {price}
                  </span>
                  <span className="text-lg font-semibold text-ds-violet-pop">
                    {currency}
                  </span>
                  {originalPrice && (
                    <span className="ml-1 text-xl text-gray-400 line-through">
                      {originalPrice}
                    </span>
                  )}
                </div>
                <span className="block text-sm text-gray-500">{priceSuffix}</span>
              </motion.div>
            </div>

            <motion.div className="mt-8" variants={itemVariants}>
              {buttonTo !== undefined ? (
                <Link
                  to={buttonTo}
                  className={cn(ACTION_CLASSES, "no-underline")}
                >
                  {buttonText}
                </Link>
              ) : (
                <button
                  type="button"
                  onClick={onButtonClick}
                  disabled={busy}
                  className={ACTION_CLASSES}
                >
                  {busy && (
                    <Loader2 className="h-4 w-4 animate-spin motion-reduce:animate-none" />
                  )}
                  {buttonText}
                </button>
              )}
            </motion.div>
          </motion.div>

          <div className="h-px w-full shrink-0 bg-ds-border lg:hidden" />

          {/* Panel derecho: lo que incluye */}
          <motion.div
            className="bg-ds-mist/30 p-6 text-left lg:w-3/5 lg:p-10"
            variants={itemVariants}
          >
            <div className="space-y-6">
              {features.map((feature, featureIndex) => (
                <div key={feature.title}>
                  <h4 className="mb-4 text-lg font-semibold text-gray-900">
                    {feature.title}:
                  </h4>
                  <ul className="grid grid-cols-1 gap-3 md:grid-cols-2">
                    {feature.items.map((item) => (
                      <motion.li
                        key={item}
                        className="flex items-start"
                        variants={listItemVariants}
                      >
                        <Check className="mr-2 mt-0.5 h-4 w-4 shrink-0 text-ds-violet-pop" />
                        <span className="text-sm text-gray-700">{item}</span>
                      </motion.li>
                    ))}
                  </ul>
                  {featureIndex < features.length - 1 && (
                    <div className="my-6 h-px w-full bg-ds-border" />
                  )}
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </motion.section>
  );
}
